import React, { useRef, useEffect } from "react";
import { Grid, keyframes, styled } from "@mui/material";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { CrewMember } from "./PairingEditorWindow/CrewMember";
import { IPositionOptions } from "src/pages/CrewSchedule/CrewScheduleHeader/CrewFilterAndSort";
import { IModalInfo } from "src/pages/CrewSchedule/CrewScheduleWindow";
import { CrewMemberBidding } from "./CrewMemberBidding";
import { defaultCrewRowSettings } from "src/pages/CrewSchedule/TimelineUtils";

interface ICrewListProps {
  id: string;
  crew: ICrewMember[];
  setCrewListRef: (ref: React.MutableRefObject<HTMLDivElement>) => void;
  crewPosition: IPositionOptions;
  bidRankSettings?: [{ prefRank: number; label: string; color: string }]; // based off of IBidSettingsValue.ranks
  bidAssignmentMode?: boolean;
  setActiveEventModal?: (modal: IModalInfo | null) => void;
  displayPlannedCredit?: boolean;
  displayAssignedNFATypes?: string[];
}

const gradientSweep = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const gradientSweepStop = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 50% 50%; }
  100% { background-position: 50% 50%; }
`;

const OCCrewList = styled(Grid)(({ theme }) => ({
  display: "flex",
  width: "100%",
  flexGrow: 1,
  border: 2,
  flexDirection: "column",
  overflow: "hidden",
  flexBasis: 0,
  "& .crew_details": {
    display: "flex",
    fontFamily: "Heebo, sans-serif",
    borderRight: "4px solid " + theme.custom.timeline.groupColumnDivider,
    transition: theme.transitions.create("border", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
    "& .expand_crew_row_btn": {
      display: "none",
      padding: 0,
      borderRadius: 3,
      "& svg": { width: 20, height: 20 },
    },
    "& div.crew_member": {
      display: "flex",
      flexGrow: 1,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-end",
      padding: "0px 5px 0px 0px",
      width: 85,
      height: defaultCrewRowSettings.crewRowHeight,
      userSelect: "none",
      "&.empty": { padding: 0 },
      "& .crew_firstname": {
        color: theme.custom.timeline.aircraftFleetColor,
        fontSize: 12,
        textAlign: "right",
        transition: theme.transitions.create("color", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
      },
      "& .crew_lastname": {
        color: theme.custom.timeline.aircraftRegColor,
        fontSize: 14,
        textAlign: "right",
      },
      "& .crew_identity, & .drop_crew": {
        color: theme.custom.timeline.aircraftTypeColor,
        fontSize: 9,
        lineHeight: "12px",
        textAlign: "right",
      },
      "& .crew_base": {
        color: theme.custom.timeline.aircraftTypeColor,
        fontSize: 12,
        lineHeight: "12px",
        textAlign: "left",
      },
      "& .crew_planned_credit": {
        fontWeight: "bold",
        color: theme.custom.timeline.aircraftRegColor,
        fontSize: 12,
        lineHeight: "12px",
        textAlign: "left",
      },
      "& .drop_crew": {
        border: `1px dashed ${theme.custom.timeline.aircraftRegColor}`,
        opacity: 0.5,
        height: "calc(100% - 10px)",
        margin: 5,
        padding: 5,
        textAlign: "center",
      },
    },
    "&.selected": {
      backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.custom.timeline.contextListBackground})`,
      borderColor: theme.custom.timeline.groupSelected,
      "& div.crew_member": {
        "& .crew_firstname": {
          color: theme.custom.timeline.groupSelected,
        },
        "& .crew_lastname": {
          color: theme.custom.timeline.aircraftTypeColor,
        },
      },
    },
    // When used in the pairing editor, interactions are disables and so should styles.
    "&.presentation:": { userSelect: "none" },
    "&:hover:not(.presentation)": {
      backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.custom.timeline.contextListBackground})`,
      "& .expand_crew_row_btn": { display: "flex" },
    },
    "&.over": {
      "& div.crew_member": {
        "& .crew_identity, & .drop_crew": {
          color: theme.custom.forms.dirtyColor,
        },
        "& .drop_crew": {
          border: `1px dashed ${theme.custom.forms.dirtyColor}`,
          color: theme.custom.forms.dirtyColor,
        },
      },
    },
    /* used to display animated background effect when actively scanning a crew member */
    "&.scan_active": {
      backgroundImage: `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.background.paper}, ${theme.palette.grey[600]}, ${theme.palette.background.paper}, ${theme.palette.background.paper})`,
      backgroundSize: "400% 400%",
      animation: `${gradientSweep} 5s ease infinite`,
      "&:hover:not(.presentation)": {
        backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.palette.grey[600]})`,
      },
    },
    "&.scan_success": {
      backgroundImage: `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.background.paper}, ${theme.palette.success.dark}, ${theme.palette.background.paper}, ${theme.palette.background.paper})`,
      backgroundSize: "400% 400%",
      animation: `${gradientSweepStop} 5s ease 1`,
      "&:hover:not(.presentation)": {
        backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.palette.success.dark})`,
      },
    },
    "&.scan_errors": {
      backgroundImage: `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.background.paper}, ${theme.palette.error.dark}, ${theme.palette.background.paper}, ${theme.palette.background.paper})`,
      backgroundSize: "400% 400%",
      animation: `${gradientSweepStop} 5s ease 1`,
      "&:hover:not(.presentation)": {
        backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.palette.error.dark})`,
      },
    },
  },
}));

export const CrewList = (props: ICrewListProps): JSX.Element => {
  const {
    id,
    crew,
    setCrewListRef,
    crewPosition,
    bidRankSettings,
    bidAssignmentMode,
    setActiveEventModal,
    displayPlannedCredit,
    displayAssignedNFATypes,
  } = props;
  const crewListView = useRef<any>();

  useEffect(() => {
    setCrewListRef(crewListView);
  }, [setCrewListRef]);

  return (
    <OCCrewList
      container
      direction="column"
      ref={crewListView}
      style={{
        height: "100%",
        overflow: "hidden",
        position: "relative",
        flexWrap: "nowrap",
      }}
      data-testid="crew-list"
    >
      {crew.map((crewMember: ICrewMember, index: number) => {
        return bidRankSettings && bidAssignmentMode ? (
          <CrewMemberBidding
            crewMember={crewMember}
            bidRankSettings={bidRankSettings}
            bidAssignmentMode={bidAssignmentMode}
            setActiveEventModal={setActiveEventModal}
            key={index}
            id={id}
            displayPlannedCredit={displayPlannedCredit}
            displayAssignedNFATypes={displayAssignedNFATypes}
          />
        ) : (
          <CrewMember crewMember={crewMember} positionStr={crewPosition?.key} key={index} id={id} />
        );
      })}
    </OCCrewList>
  );
};
